import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiClientError, isMockMode } from "../../api/client";
import { fetchAffAffiliates, fetchAffMonthly, fetchAffPayments, fetchGfBoard } from "../../api/giftingClient";
import { jstMonthOf } from "../../lib/giftingRules";
import type { AffAffiliate, AffMonthlyReport, AffPaymentsResponse, GfBoardResponse, GfData, Loadable } from "../../types/gifting";

/** mock（?mock=1）の「いま」。経過日数の判定が毎回ぶれないよう固定する（giftingEngine の NOW と同じ値）。 */
export const GF_MOCK_NOW = "2026-10-05T03:00:00.000Z";

function msg(e: unknown): string {
  return e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。";
}

export interface GiftingDataState {
  /** board の読込状態（画面全体のゲート） */
  board: Loadable<GfBoardResponse>;
  /** board が読めたときだけ non-null（affiliate 由来は個別 Loadable） */
  data: GfData | null;
  nowIso: string;
  month: string;
  /** board と affiliate を再取得（既存表示は消さない） */
  refresh: () => Promise<void>;
  /** 初回読込の再試行（board を loading に戻してから取り直す） */
  retry: () => void;
  /** 再取得に失敗したときのメッセージ（前回データは残す） */
  refreshError: string | null;
}

export function useGiftingData(): GiftingDataState {
  const [board, setBoard] = useState<Loadable<GfBoardResponse>>({ status: "loading" });
  const [affiliates, setAffiliates] = useState<Loadable<AffAffiliate[]>>({ status: "loading" });
  const [monthly, setMonthly] = useState<Loadable<AffMonthlyReport>>({ status: "loading" });
  const [payments, setPayments] = useState<Loadable<AffPaymentsResponse>>({ status: "loading" });
  const [nowIso, setNowIso] = useState<string>(() => (isMockMode() ? GF_MOCK_NOW : new Date().toISOString()));
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const seq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const id = ++seq.current;
    const alive = () => id === seq.current;

    const affTask = fetchAffAffiliates().then(
      (d) => {
        if (alive()) setAffiliates({ status: "ok", data: d });
      },
      (e: unknown) => {
        if (alive()) setAffiliates({ status: "error", message: msg(e) });
      }
    );

    let b: GfBoardResponse;
    try {
      b = await fetchGfBoard();
    } catch (e) {
      if (alive()) {
        setBoard((prev) => (prev.status === "ok" ? prev : { status: "error", message: msg(e) }));
        setRefreshError(msg(e));
      }
      await affTask;
      return;
    }
    if (!alive()) return;
    const now = isMockMode() ? GF_MOCK_NOW : b.server_now;
    const month = jstMonthOf(now);
    setNowIso(now);
    setBoard({ status: "ok", data: b });
    setRefreshError(null);

    const monthlyTask = fetchAffMonthly(month).then(
      (d) => {
        if (alive()) setMonthly({ status: "ok", data: d });
      },
      (e: unknown) => {
        if (alive()) setMonthly({ status: "error", message: msg(e) });
      }
    );
    const paymentsTask = fetchAffPayments(month).then(
      (d) => {
        if (alive()) setPayments({ status: "ok", data: d });
      },
      (e: unknown) => {
        if (alive()) setPayments({ status: "error", message: msg(e) });
      }
    );
    await Promise.all([affTask, monthlyTask, paymentsTask]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const retry = useCallback(() => {
    setBoard({ status: "loading" });
    setAffiliates({ status: "loading" });
    setMonthly({ status: "loading" });
    setPayments({ status: "loading" });
    void load();
  }, [load]);

  const data = useMemo<GfData | null>(
    () => (board.status === "ok" ? { board: board.data, affiliates, monthly, payments } : null),
    [board, affiliates, monthly, payments]
  );
  return { board, data, nowIso, month: jstMonthOf(nowIso), refresh: load, retry, refreshError };
}
